import React, { useRef, useState } from 'react';
import { Box, Dialog, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { photoUrl } from '../services/postService';
import { isVideoKey } from '../utils/media';
import PostVideo from './PostVideo';

interface PostMediaProps {
  mediaKeys: string[];
}

/**
 * A post's photos and short videos. One item renders full-width; several become a swipeable
 * carousel (native scroll-snap, so touch swipe just works) with arrows and a counter. Photos
 * open in a full-screen viewer on click; videos play inline with native controls.
 */
const PostMedia: React.FC<PostMediaProps> = ({ mediaKeys }) => {
  const scroller = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [lightboxKey, setLightboxKey] = useState<string | null>(null);

  if (mediaKeys.length === 0) return null;
  const multiple = mediaKeys.length > 1;

  const goTo = (next: number) => {
    const el = scroller.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(mediaKeys.length - 1, next));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    setIndex(clamped);
  };

  const handleScroll = () => {
    const el = scroller.current;
    if (!el || el.clientWidth === 0) return;
    setIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  const arrowSx = {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    bgcolor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
  };

  return (
    <Box sx={{ position: 'relative', mt: 1.5 }}>
      <Box
        ref={scroller}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: multiple ? 'auto' : 'hidden',
          scrollSnapType: 'x mandatory',
          borderRadius: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {mediaKeys.map(key => (
          <Box key={key} sx={{ flex: '0 0 100%', scrollSnapAlign: 'start', bgcolor: 'common.black', display: 'flex', justifyContent: 'center' }}>
            {isVideoKey(key) ? (
              <PostVideo
                videoKey={key}
                controls
                playsInline
                preload="metadata"
                style={{ width: '100%', maxHeight: 520, display: 'block' }}
              />
            ) : (
              <img
                src={photoUrl(key)}
                alt=""
                loading="lazy"
                onClick={() => setLightboxKey(key)}
                style={{ width: '100%', maxHeight: 520, objectFit: 'contain', display: 'block', cursor: 'zoom-in' }}
              />
            )}
          </Box>
        ))}
      </Box>

      {multiple && (
        <>
          {index > 0 && (
            <IconButton size="small" aria-label="Previous" onClick={() => goTo(index - 1)} sx={{ ...arrowSx, left: 8 }}>
              <ChevronLeft />
            </IconButton>
          )}
          {index < mediaKeys.length - 1 && (
            <IconButton size="small" aria-label="Next" onClick={() => goTo(index + 1)} sx={{ ...arrowSx, right: 8 }}>
              <ChevronRight />
            </IconButton>
          )}
          <Box sx={{ position: 'absolute', top: 8, right: 8, px: 1, py: 0.25, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, fontWeight: 700 }}>
            {index + 1} / {mediaKeys.length}
          </Box>
        </>
      )}

      <Dialog open={!!lightboxKey} onClose={() => setLightboxKey(null)} maxWidth={false} PaperProps={{ sx: { bgcolor: 'common.black', boxShadow: 'none', maxWidth: '96vw' } }}>
        <IconButton aria-label="Close" onClick={() => setLightboxKey(null)} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}>
          <Close />
        </IconButton>
        {lightboxKey && <img src={photoUrl(lightboxKey)} alt="" style={{ maxWidth: '96vw', maxHeight: '92vh', objectFit: 'contain', display: 'block' }} />}
      </Dialog>
    </Box>
  );
};

export default PostMedia;
